import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { DASHBOARD, AUTH } from '../constants/testIds';
import { Search, MapPin, Star, LogOut, Car, Package, Utensils, CheckCircle2, X, MessageCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ThemeToggle from '../components/ThemeToggle';
import RideRequestModal from '../components/RideRequestModal';
import DeliveryRequestModal from '../components/DeliveryRequestModal';
import RestaurantMarketplace from '../components/RestaurantMarketplace';
import GoogleRideTrackingMap from '../components/GoogleRideTrackingMap';
import ChatInterface from '../components/ChatInterface';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getStatusLabel } from '../utils/statusLabels';
import {
  getClientVisibilityKey,
  getPersistentOrderId,
  isHideableExpiredRide,
  readHiddenExpiredOrderIds,
  writeHiddenExpiredOrderIds
} from '../utils/clientExpiredOrderVisibility';

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRideModal, setShowRideModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showFood, setShowFood] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [myOrders, setMyOrders] = useState({ rides: [], deliveries: [] });
  const [rideOffers, setRideOffers] = useState({});
  const [rideRatings, setRideRatings] = useState({});
  const [toast, setToast] = useState(null);
  const [highlightedOffers, setHighlightedOffers] = useState({});
  const [rideEtas, setRideEtas] = useState({});
  const [chatRide, setChatRide] = useState(null);
  const [editingRideOffers, setEditingRideOffers] = useState({});
  const [savingRideOffer, setSavingRideOffer] = useState({});
  const clientVisibilityKey = getClientVisibilityKey(user);
  const [hiddenExpiredOrderIds, setHiddenExpiredOrderIds] = useState(() => readHiddenExpiredOrderIds(user));
  const [dismissingExpiredOrders, setDismissingExpiredOrders] = useState({});
  const [swipeOffsets, setSwipeOffsets] = useState({});
  const previousOfferPricesRef = useRef({});
  const hiddenExpiredOrderIdsRef = useRef(hiddenExpiredOrderIds);
  const swipeGestureRef = useRef(null);
  const dismissTimersRef = useRef([]);
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    ride: null,
    reason: '',
    description: '',
    evidence_url: '',
    evidence_type: ''
  });
  const [simpleCancelDialog, setSimpleCancelDialog] = useState({
    open: false,
    ride: null
  });
  const [submittingCancel, setSubmittingCancel] = useState(false);

  const clientCancelReasons = [
    { key: 'personal_emergency', label: 'Emergencia personal', level: 'normal' },
    { key: 'health_problem', label: 'Problema de salud', level: 'normal' },
    { key: 'wrong_pickup', label: 'Punto de recogida incorrecto', level: 'normal' },
    { key: 'driver_delay', label: 'El conductor tardó demasiado', level: 'normal' },
    { key: 'changed_plans', label: 'Cambio de planes', level: 'normal' },
    { key: 'unsafe', label: 'Me siento inseguro', level: 'strong' },
    { key: 'route_changed_without_explanation', label: 'El conductor cambió la ruta sin explicación', level: 'strong' },
    { key: 'dangerous_driving', label: 'Conducción peligrosa', level: 'strong' },
    { key: 'vehicle_mismatch', label: 'El vehículo no coincide', level: 'strong' },
    { key: 'driver_mismatch', label: 'El conductor no coincide', level: 'strong' },
    { key: 'harassment', label: 'Acoso', level: 'strong' },
    { key: 'threat', label: 'Amenaza', level: 'strong' },
    { key: 'aggression', label: 'Agresión', level: 'strong' },
    { key: 'suspicious_detour', label: 'Desvío sospechoso', level: 'strong' },
    { key: 'other', label: 'Otro motivo', level: 'normal' }
  ];

  const isUnassignedTaxiRequest = (ride) => {
    const status = String(ride.status || '').toLowerCase();

    const simpleStatuses = [
      'pending',
      'offer_received',
      'searching',
      'requested',
      'waiting_driver',
      'looking_for_driver',
      'pending_driver',
      'created',
      'buscando_taxi',
      'buscando taxi'
    ];

    const hasDriver =
      ride.provider_id ||
      ride.driver_id ||
      ride.assigned_driver_id ||
      ride.provider_name ||
      ride.driver;

    return simpleStatuses.includes(status) && !hasDriver;
  };

  useEffect(() => {
    loadProviders();
    loadMyOrders();
    loadServiceCategories();
    const interval = setInterval(loadMyOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const storedIds = readHiddenExpiredOrderIds({ id: clientVisibilityKey });
    hiddenExpiredOrderIdsRef.current = storedIds;
    setHiddenExpiredOrderIds(storedIds);
  }, [clientVisibilityKey]);

  useEffect(() => () => {
    dismissTimersRef.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const loadServiceCategories = async () => {
    try {
      const response = await axios.get(`${API}/service-categories`, { withCredentials: true });
      setServiceCategories(response.data || []);
    } catch (error) {
      console.error('Error loading service categories:', error);
    }
  };

  const loadProviders = async () => {
    try {
      const response = await axios.get(`${API}/providers?approved=true&active_only=true`, { withCredentials: true });
      setProviders(response.data);
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyOrders = async () => {
    try {
      const [rides, deliveries] = await Promise.all([
        axios.get(`${API}/rides/my`, { withCredentials: true }),
        axios.get(`${API}/delivery/my`, { withCredentials: true })
      ]);
      setMyOrders({ rides: rides.data, deliveries: deliveries.data });
      const offerPairs = await Promise.all(
        (rides.data || [])
          .filter((ride) => ['pending', 'offer_received'].includes(ride.status))
          .map(async (ride) => {
            try {
              const { data } = await axios.get(`${API}/rides/${ride.id}/offers`, { withCredentials: true });
              return [ride.id, data || []];
            } catch (error) {
              return [ride.id, []];
            }
          })
      );
      const nextOffers = Object.fromEntries(offerPairs.map(([rideId, offers]) => [rideId, dedupeOffers(offers)]));
      const changed = {};
      Object.entries(nextOffers).forEach(([rideId, offers]) => {
        offers.forEach((offer) => {
          const key = `${rideId}:${offer.provider_id || offer.id}`;
          const previous = previousOfferPricesRef.current[key];
          const current = Number(offer.offered_price || 0);
          if (previous !== undefined && current !== previous) changed[key] = true;
          previousOfferPricesRef.current[key] = current;
        });
      });
      if (Object.keys(changed).length > 0) {
        setHighlightedOffers((current) => ({ ...current, ...changed }));
        window.setTimeout(() => {
          setHighlightedOffers((current) => {
            const next = { ...current };
            Object.keys(changed).forEach((key) => delete next[key]);
            return next;
          });
        }, 500);
      }
      setRideOffers(nextOffers);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const dedupeOffers = (offers = []) => {
    const byProvider = new Map();
    offers
      .filter((offer) => offer.status === 'pending')
      .forEach((offer) => {
        const key = offer.provider_id || offer.id;
        const current = byProvider.get(key);
        const currentDate = new Date(current?.updated_at || current?.created_at || 0).getTime();
        const nextDate = new Date(offer.updated_at || offer.created_at || 0).getTime();
        if (!current || nextDate >= currentDate) byProvider.set(key, offer);
      });
    return Array.from(byProvider.values()).sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
  };

  const showToast = (title, text) => {
    setToast({ title, text });
    window.setTimeout(() => setToast(null), 3000);
  };

  const hideExpiredOrder = (order) => {
    if (!isHideableExpiredRide(order)) return;
    const orderId = getPersistentOrderId(order);
    if (hiddenExpiredOrderIdsRef.current.includes(orderId)) return;

    const nextIds = [...hiddenExpiredOrderIdsRef.current, orderId];
    hiddenExpiredOrderIdsRef.current = nextIds;
    writeHiddenExpiredOrderIds(user, nextIds);
    setSwipeOffsets((current) => ({ ...current, [orderId]: 120 }));
    setDismissingExpiredOrders((current) => ({ ...current, [orderId]: true }));
    showToast('Pedido ocultado', 'Pedido ocultado de la vista principal');

    const timer = window.setTimeout(() => {
      setHiddenExpiredOrderIds([...hiddenExpiredOrderIdsRef.current]);
      setDismissingExpiredOrders((current) => {
        const next = { ...current };
        delete next[orderId];
        return next;
      });
    }, 180);
    dismissTimersRef.current.push(timer);
  };

  const startExpiredOrderSwipe = (event, order) => {
    if (!isHideableExpiredRide(order)) return;
    const touch = event.touches[0];
    swipeGestureRef.current = {
      orderId: getPersistentOrderId(order),
      startX: touch.clientX,
      startY: touch.clientY
    };
  };

  const moveExpiredOrderSwipe = (event, order) => {
    const gesture = swipeGestureRef.current;
    const orderId = getPersistentOrderId(order);
    if (!gesture || gesture.orderId !== orderId) return;
    const touch = event.touches[0];
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    if (deltaX <= 0 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    setSwipeOffsets((current) => ({ ...current, [orderId]: Math.min(deltaX, 120) }));
  };

  const finishExpiredOrderSwipe = (event, order) => {
    const gesture = swipeGestureRef.current;
    const orderId = getPersistentOrderId(order);
    swipeGestureRef.current = null;
    if (!gesture || gesture.orderId !== orderId) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    if (deltaX >= 84 && deltaX > Math.abs(deltaY)) {
      hideExpiredOrder(order);
      return;
    }
    setSwipeOffsets((current) => ({ ...current, [orderId]: 0 }));
  };

  const handleRideEtaChange = useCallback((rideId, eta) => {
    setRideEtas((current) => {
      if (!eta) {
        if (!current[rideId]) return current;
        const next = { ...current };
        delete next[rideId];
        return next;
      }
      const previous = current[rideId];
      if (previous?.minutes === eta.minutes && previous?.distance_km === eta.distance_km && previous?.phase === eta.phase) {
        return current;
      }
      return { ...current, [rideId]: eta };
    });
  }, []);

  const acceptRideOffer = async (rideId, offerId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/offers/${offerId}/accept`, {}, { withCredentials: true });
      loadMyOrders();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al aceptar oferta');
    }
  };

  const rejectRideOffer = async (rideId, offerId) => {
    if (!window.confirm('¿Seguro que quieres rechazar esta oferta?')) return;
    try {
      await axios.post(`${API}/rides/${rideId}/offers/${offerId}/reject`, {}, { withCredentials: true });
      setRideOffers((current) => ({
        ...current,
        [rideId]: (current[rideId] || []).filter((offer) => offer.id !== offerId)
      }));
      loadMyOrders();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al rechazar oferta');
    }
  };

  const rateRide = async (rideId) => {
    const rating = Number(rideRatings[rideId]?.rating || 5);
    try {
      await axios.post(`${API}/rides/${rideId}/rate`, {
        rating,
        comment: rideRatings[rideId]?.comment || ''
      }, { withCredentials: true });
      loadMyOrders();
      alert('Gracias por calificar tu viaje.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al calificar viaje');
    }
  };

  const closeCancelRideDialog = () => {
    setCancelDialog({
      open: false,
      ride: null,
      reason: '',
      description: '',
      evidence_url: '',
      evidence_type: ''
    });
  };

  const closeSimpleCancelDialog = () => {
    setSimpleCancelDialog({
      open: false,
      ride: null
    });
  };

  const openCancelRideDialog = (ride) => {
    if (isUnassignedTaxiRequest(ride)) {
      setSimpleCancelDialog({
        open: true,
        ride
      });
      return;
    }

    const defaultReason = ['pending', 'offer_received'].includes(ride.status) ? 'changed_plans' : '';
    setCancelDialog({
      open: true,
      ride,
      reason: defaultReason,
      description: '',
      evidence_url: '',
      evidence_type: ''
    });
  };

  const updateCancelDialog = (field, value) => {
    setCancelDialog((current) => ({ ...current, [field]: value }));
  };

  const getCancelDescriptionWordCount = () => {
    return (cancelDialog.description || '').trim().split(/\s+/).filter(Boolean).length;
  };

  const submitCancelRide = async () => {
    const ride = cancelDialog.ride;
    if (!ride) return;

    if (!cancelDialog.reason) {
      alert('Selecciona un motivo de cancelación.');
      return;
    }

    const description = (cancelDialog.description || '').trim();
    const evidenceUrl = (cancelDialog.evidence_url || '').trim();
    const evidenceType = cancelDialog.evidence_type || '';

    if (ride.status === 'in_progress' && getCancelDescriptionWordCount() < 20) {
      alert('Describe la situación con al menos 20 palabras para cancelar un viaje en curso.');
      return;
    }

    if (evidenceUrl && !evidenceType) {
      alert('Selecciona si la evidencia es foto o audio.');
      return;
    }

    setSubmittingCancel(true);
    try {
      await axios.post(`${API}/rides/${ride.id}/cancel`, {
        reason: cancelDialog.reason,
        description,
        evidence_url: evidenceUrl || null,
        evidence_type: evidenceUrl ? evidenceType : null
      }, { withCredentials: true });

      closeCancelRideDialog();
      loadMyOrders();
      showToast('Viaje cancelado', 'Tu cancelación quedó registrada para revisión si corresponde.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cancelar solicitud');
    } finally {
      setSubmittingCancel(false);
    }
  };

  const submitSimpleCancelRide = async () => {
    const ride = simpleCancelDialog.ride;
    if (!ride) return;

    setSubmittingCancel(true);
    try {
      await axios.post(`${API}/rides/${ride.id}/cancel`, {
        reason: 'client_cancelled_before_driver_assigned',
        description: 'Solicitud cancelada por el cliente antes de asignar conductor.',
        evidence_url: null,
        evidence_type: null
      }, { withCredentials: true });

      closeSimpleCancelDialog();
      loadMyOrders();
      showToast('Solicitud cancelada', 'Tu solicitud fue cancelada antes de asignar conductor.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cancelar solicitud');
    } finally {
      setSubmittingCancel(false);
    }
  };

  const getRideEditPrice = (ride) => {
    const draft = editingRideOffers[ride.id];
    if (draft !== undefined && draft !== null && draft !== '') return Number(draft);
    return Number(ride.offered_price || ride.suggested_price || 0.5);
  };

  const startEditRideOffer = (ride) => {
    setEditingRideOffers((current) => ({
      ...current,
      [ride.id]: Number(ride.offered_price || ride.suggested_price || 0.5).toFixed(2)
    }));
  };

  const adjustRideEditOffer = (ride, delta) => {
    const current = getRideEditPrice(ride);
    const next = Math.max(0.5, current + Number(delta || 0));
    setEditingRideOffers((state) => ({ ...state, [ride.id]: Number(next.toFixed(2)).toFixed(2) }));
  };

  const updateRideClientOffer = async (ride) => {
    const price = Number(getRideEditPrice(ride));
    if (!Number.isFinite(price) || price < 0.5) {
      alert('La oferta mínima es $0.50');
      return;
    }
    setSavingRideOffer((current) => ({ ...current, [ride.id]: true }));
    try {
      await axios.put(`${API}/rides/${ride.id}/client-offer`, {
        offered_price: Number(price.toFixed(2)),
        payment_method: ride.payment_method,
        cash_bill: ride.cash_bill
      }, { withCredentials: true });
      setEditingRideOffers((current) => {
        const next = { ...current };
        delete next[ride.id];
        return next;
      });
      showToast('Oferta actualizada', `Tu nueva oferta es $${price.toFixed(2)}. Los taxistas verán el cambio.`);
      loadMyOrders();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar oferta');
    } finally {
      setSavingRideOffer((current) => ({ ...current, [ride.id]: false }));
    }
  };

  const formatVehicleInfo = (vehicleInfo) => {
    if (!vehicleInfo) return { vehicle: 'Vehiculo registrado', plate: 'Placa pendiente' };
    if (typeof vehicleInfo === 'string') return { vehicle: vehicleInfo || 'Vehiculo registrado', plate: 'Placa pendiente' };
    return {
      vehicle: vehicleInfo.model || vehicleInfo.vehicle || vehicleInfo.type || 'Vehiculo registrado',
      plate: vehicleInfo.plate || vehicleInfo.vehicle_plate || vehicleInfo.vehicle_plate_1 || vehicleInfo.placa || 'Placa pendiente'
    };
  };

  const getTaxiStatusText = (status) => ({
    accepted: 'Conductor aceptado',
    driver_on_way: 'Conductor en camino',
    arrived: 'El conductor llego',
    in_progress: 'Viaje en curso',
    completed: 'Viaje completado',
    cancelled: 'Cancelado'
  })[status] || getStatusLabel(status, 'taxi');

  const getClientEtaText = (ride) => {
    const eta = rideEtas[ride.id];
    if (!ride.provider_location && ['accepted', 'driver_on_way', 'in_progress'].includes(ride.status)) {
      return 'Esperando ubicacion del conductor';
    }
    if (!eta) return 'Calculando ETA...';
    if (['accepted', 'driver_on_way'].includes(ride.status)) return `El taxi llega en ${eta.minutes} min`;
    if (ride.status === 'in_progress') return `Llegas al destino en ${eta.minutes} min`;
    if (ride.status === 'arrived') return 'El conductor llego';
    return `${eta.minutes} min`;
  };

  const SearchingTaxiSonar = () => (
    <div className="relative w-20 h-20 flex items-center justify-center" aria-label="Buscando taxistas">
      <div className="absolute inset-0 rounded-full border border-green-400/40 animate-ping" />
      <div className="absolute inset-2 rounded-full border border-green-300/30 animate-pulse" />
      <div className="absolute inset-4 rounded-full bg-green-400/10 border border-green-300/60" />
      <div className="w-3 h-3 rounded-full bg-green-300 shadow-[0_0_18px_rgba(74,222,128,0.9)]" />
      <div className="absolute h-px w-8 bg-gradient-to-r from-green-300 to-transparent origin-left animate-spin" />
    </div>
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const filteredProviders = providers.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeOrders = [
    ...myOrders.rides.filter(r => {
      if (['cancelled'].includes(r.status) || (r.status === 'completed' && r.rating_by_client)) return false;
      const orderId = getPersistentOrderId(r);
      return !isHideableExpiredRide(r) || !hiddenExpiredOrderIds.includes(orderId);
    }),
    ...myOrders.deliveries.filter(d => !['delivered', 'cancelled'].includes(d.status))
  ];

  const categoryByKey = (key) => serviceCategories.find((category) => category.key === key) || {};
  const serviceCards = [
    { key: 'taxi', fallbackName: 'Taxi', fallbackSubtitle: 'Pide un viaje con precio acordado', icon: Car, color: 'from-[#FF007F] to-[#8A2BE2]', action: () => setShowRideModal(true) },
    { key: 'delivery', fallbackName: 'Delivery', fallbackSubtitle: 'Encomiendas, compras y entregas', icon: Package, color: 'from-[#00F0FF] to-[#8A2BE2]', action: () => setShowDeliveryModal(true) },
    { key: 'food', fallbackName: 'Comida', fallbackSubtitle: 'Restaurantes y promociones', icon: Utensils, color: 'from-[#FF007F] to-[#00F0FF]', action: () => setShowFood(true) }
  ];

  if (showFood) {
    return (
      <RestaurantMarketplace
        onBack={() => setShowFood(false)}
        onOrderCreated={() => loadMyOrders()}
      />
    );
  }

  return (
    <div className="min-h-screen relative" data-testid={DASHBOARD.clientDashboard}>
      <div className="mesh-glow-purple" style={{ top: '0%', left: '0%' }} />
      <div className="mesh-glow-blue" style={{ bottom: '0%', right: '0%' }} />
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-2rem)] max-w-sm bg-[#0B0C10]/95 border border-green-400/30 shadow-[0_0_28px_rgba(34,197,94,0.18)] rounded-2xl p-4 text-white">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">{toast.title}</p>
              <p className="text-sm text-[#D1D5DB]">{toast.text}</p>
            </div>
          </div>
        </div>
      )}

      <header className="relative z-10 border-b border-white/10 bg-black/20 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
              Explora Servicios
            </h1>
            <p className="text-sm text-[#9CA3AF]">{user?.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button
              data-testid={AUTH.logoutButton}
              onClick={handleLogout}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Main service cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {serviceCards.map((card) => {
            const config = categoryByKey(card.key);
            const enabled = config.enabled !== false;
            const Icon = card.icon;
            return (
              <button
                key={card.key}
                data-testid={`client-service-${card.key}`}
                onClick={() => enabled && card.action()}
                disabled={!enabled}
                className={`glass-card p-6 rounded-3xl transition-all duration-300 text-left group ${enabled ? 'hover:scale-[1.02]' : 'opacity-50 cursor-not-allowed'}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 bg-gradient-to-r ${card.color} rounded-2xl flex items-center justify-center`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-medium text-white" style={{ fontFamily: 'Outfit' }}>{config.name || card.fallbackName}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${enabled ? 'text-green-300 border-green-500/30 bg-green-500/10' : 'text-yellow-300 border-yellow-500/30 bg-yellow-500/10'}`}>
                        {enabled ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <p className="text-sm text-[#9CA3AF]">{config.subtitle || card.fallbackSubtitle}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active Orders */}
        {activeOrders.length > 0 && (
          <div className="glass-card p-5 rounded-3xl mb-8" data-testid="client-active-orders">
            <h3 className="text-lg font-medium text-white mb-3" style={{ fontFamily: 'Outfit' }}>
              Pedidos Activos
            </h3>
            <div className="space-y-3">
              {activeOrders.map((o) => {
                const isRide = !o.service_type;
                const isHideableExpired = isRide && isHideableExpiredRide(o);
                const persistentOrderId = getPersistentOrderId(o);
                const swipeOffset = swipeOffsets[persistentOrderId] || 0;
                const swipeRevealOpacity = Math.min(swipeOffset / 84, 1);
                const offers = rideOffers[o.id] || [];
                const isSearchingRide = isRide && ['pending', 'offer_received'].includes(o.status);
                const editingOfferValue = editingRideOffers[o.id];
                return (
                  <div
                    key={o.id}
                    className="relative min-w-0 overflow-hidden rounded-2xl"
                    data-testid={`client-active-order-shell-${persistentOrderId}`}
                  >
                    {isHideableExpired && (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 flex items-center justify-end gap-2 rounded-2xl border border-red-400/20 bg-red-950/80 px-5 text-sm font-medium text-red-100 transition-opacity duration-200 lg:hidden"
                        style={{
                          opacity: swipeRevealOpacity,
                          visibility: swipeOffset > 0 ? 'visible' : 'hidden'
                        }}
                        data-testid={`expired-order-swipe-reveal-${persistentOrderId}`}
                      >
                        <X className="h-5 w-5" aria-hidden="true" />
                        Ocultar
                      </div>
                    )}
                    <div
                      className={`relative min-w-0 rounded-2xl border border-white/10 bg-[#1B1D22] p-4 transition-all duration-200 ${dismissingExpiredOrders[persistentOrderId] ? 'opacity-0' : 'opacity-100'}`}
                      style={{
                        transform: `translateX(${swipeOffset}px)`,
                        touchAction: 'pan-y'
                      }}
                      onTouchStart={(event) => startExpiredOrderSwipe(event, o)}
                      onTouchMove={(event) => moveExpiredOrderSwipe(event, o)}
                      onTouchEnd={(event) => finishExpiredOrderSwipe(event, o)}
                      data-testid={`client-active-order-${persistentOrderId}`}
                    >
                    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-sm text-white">{isRide ? 'Viaje en taxi' : `Delivery ${o.service_type}`}</p>
                        {!isHideableExpired && (
                          <p className="text-xs text-[#9CA3AF]">Estado: {getStatusLabel(o.status, isRide ? 'taxi' : 'delivery')}</p>
                        )}
                        {isRide && (
                          <p className="text-xs text-[#D1D5DB] mt-1">
                            Oferta ${Number(o.offered_price || 0).toFixed(2)} · Sugerido ${Number(o.suggested_price || o.offered_price || 0).toFixed(2)}
                          </p>
                        )}
                        {isRide && o.provider_name && (
                          <p className="text-xs text-[#D1D5DB] mt-1">Conductor asignado: {o.provider_name}</p>
                        )}
                      </div>
                      <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:justify-end">
                        <span className="flex min-h-[32px] min-w-0 flex-1 items-center justify-center rounded-lg border border-yellow-500/30 bg-yellow-500/20 px-3 py-1 text-center text-xs text-yellow-400 sm:flex-none sm:whitespace-nowrap">
                          {getStatusLabel(o.status, isRide ? 'taxi' : 'delivery')}
                        </span>
                        {isHideableExpired && (
                          <button
                            type="button"
                            onClick={() => hideExpiredOrder(o)}
                            aria-label="Ocultar pedido caducado"
                            title="Ocultar de la vista principal"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-[#111318] text-[#D1D5DB] transition-colors hover:border-red-400/30 hover:bg-red-500/15 hover:text-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300"
                            data-testid={`hide-expired-order-${persistentOrderId}`}
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </div>

                    {isSearchingRide && (
                      <div className="mt-4 rounded-2xl border border-green-400/20 bg-green-500/5 p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <SearchingTaxiSonar />
                          <div className="flex-1">
                            <p className="text-white font-semibold">Buscando taxistas cercanos</p>
                            <p className="text-sm text-[#D1D5DB] mt-1">
                              Tu solicitud sigue activa. Puedes mejorar tu oferta sin cancelar el pedido.
                            </p>
                            <p className="text-xs text-green-300 mt-2">
                              Oferta actual: ${Number(o.offered_price || 0).toFixed(2)} · Sugerido: ${Number(o.suggested_price || o.offered_price || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button onClick={() => openCancelRideDialog(o)} className="bg-red-500/15 border border-red-500/30 text-red-200 hover:bg-red-500/25 rounded-xl">
                            <X className="w-4 h-4 mr-2" />
                            Cancelar solicitud
                          </Button>
                          <Button onClick={() => startEditRideOffer(o)} className="bg-white/5 border border-green-400/30 text-green-100 hover:bg-green-500/10 rounded-xl">
                            Editar oferta
                          </Button>
                        </div>

                        {editingOfferValue !== undefined && (
                          <div className="mt-4 rounded-2xl bg-black/25 border border-white/10 p-3">
                            <p className="text-sm text-white font-medium mb-2">Editar oferta del cliente</p>
                            <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-center">
                              <Button type="button" onClick={() => adjustRideEditOffer(o, -0.25)} className="bg-green-900/70 border border-green-700/60 text-green-100 hover:bg-green-800 rounded-xl min-h-[44px]">
                                -$0.25
                              </Button>
                              <div className="text-center rounded-xl bg-white/5 border border-white/10 py-2">
                                <p className="text-2xl text-white font-semibold">${getRideEditPrice(o).toFixed(2)}</p>
                              </div>
                              <Button type="button" onClick={() => adjustRideEditOffer(o, 0.25)} className="bg-green-500/25 border border-green-400/40 text-green-100 hover:bg-green-500/35 rounded-xl min-h-[44px]">
                                +$0.25
                              </Button>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button disabled={savingRideOffer[o.id]} onClick={() => updateRideClientOffer(o)} className="gradient-button rounded-xl">
                                {savingRideOffer[o.id] ? 'Guardando...' : 'Actualizar oferta'}
                              </Button>
                              <Button onClick={() => setEditingRideOffers((current) => { const next = { ...current }; delete next[o.id]; return next; })} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-xl">
                                Cerrar edición
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {isRide && o.provider_name && ['accepted', 'driver_on_way', 'arrived', 'in_progress'].includes(o.status) && (() => {
                      const vehicle = formatVehicleInfo(o.vehicle_info);
                      return (
                        <div className="mt-4 bg-[#0B0C10]/80 border border-[#00F0FF]/25 rounded-2xl p-4 shadow-[0_0_24px_rgba(0,240,255,0.08)]">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#8A2BE2] flex items-center justify-center text-white font-semibold">
                                {(o.provider_name || 'C').slice(0, 1).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-[0.18em] text-green-300 flex items-center gap-1">
                                  <CheckCircle2 className="w-4 h-4" />
                                  {getTaxiStatusText(o.status)}
                                </p>
                                <p className="text-white font-semibold">{o.provider_name}</p>
                                <p className="text-xs text-[#D1D5DB]">
                                  {Number(o.provider_rating || 0).toFixed(1)} estrellas - {vehicle.vehicle} - {vehicle.plate}
                                </p>
                                <p className="text-xs text-[#00F0FF] flex items-center gap-1 mt-1">
                                  <Clock className="w-3.5 h-3.5" />
                                  {getClientEtaText(o)}
                                </p>
                              </div>
                            </div>
                            <div className="sm:text-right">
                              <p className="text-xs text-[#9CA3AF]">Precio final</p>
                              <p className="text-2xl text-white font-semibold">${Number(o.final_price || o.offered_price || 0).toFixed(2)}</p>
                              <p className="text-xs text-[#D1D5DB]">{o.payment_method === 'transfer' ? 'Transferencia' : 'Efectivo'}</p>
                            </div>
                          </div>
                          {o.ride_pin && ['accepted', 'arrived'].includes(o.status) && (
                            <div className="mt-3 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-xl p-3">
                              <p className="text-xs text-[#D1D5DB]">Comparte tu PIN solo cuando estes en el taxi correcto.</p>
                              <p className="text-3xl text-white font-semibold tracking-widest">{o.ride_pin}</p>
                            </div>
                          )}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button onClick={() => setChatRide(o)} className="relative bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-xl">
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Chat
                              {Number(o.unread_messages_count || 0) > 0 && (
                                <span className="ml-2 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[11px] flex items-center justify-center">
                                  {o.unread_messages_count}
                                </span>
                              )}
                            </Button>
                            <Button disabled className="bg-white/5 border border-white/10 text-[#D1D5DB] rounded-xl opacity-100">
                              Estado: {getTaxiStatusText(o.status)}
                            </Button>
                            {['accepted', 'driver_on_way', 'arrived', 'in_progress'].includes(o.status) && (
                              <Button onClick={() => openCancelRideDialog(o)} className="bg-gradient-to-r from-red-500 to-[#FF007F] hover:opacity-90 text-white rounded-xl">
                                <X className="w-4 h-4 mr-2" />
                                Cancelar
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {isRide && offers.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs text-[#9CA3AF] uppercase tracking-wide">Ofertas recibidas</p>
                        {offers.map((offer) => {
                          const highlightKey = `${o.id}:${offer.provider_id || offer.id}`;
                          return (
                          <div
                            key={offer.id}
                            className={`flex flex-wrap items-center justify-between gap-2 rounded-xl p-3 transition-all duration-300 ${
                              highlightedOffers[highlightKey]
                                ? 'bg-green-500/15 border border-green-400/40 scale-[1.01]'
                                : 'bg-black/20 border border-transparent'
                            }`}
                          >
                            <div>
                              <p className="text-sm text-white">{offer.provider_name || 'Conductor'}</p>
                              <p className="text-xs text-[#9CA3AF]">{offer.offer_type === 'counteroffer' ? 'Contraoferta' : 'Acepta tu precio'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {offer.previous_price && Number(offer.previous_price) !== Number(offer.offered_price) && (
                                <span className="text-sm text-[#9CA3AF] line-through">${Number(offer.previous_price || 0).toFixed(2)}</span>
                              )}
                              <span className="text-2xl sm:text-3xl text-[#FF007F] font-bold leading-none">${Number(offer.offered_price || 0).toFixed(2)}</span>
                              <Button onClick={() => acceptRideOffer(o.id, offer.id)} className="gradient-button rounded-xl">Aceptar</Button>
                              <Button onClick={() => rejectRideOffer(o.id, offer.id)} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-xl">Rechazar</Button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {isRide && ['accepted', 'driver_on_way', 'arrived', 'in_progress'].includes(o.status) && (
                      <div className="mt-3">
                        <GoogleRideTrackingMap
                          pickup={o.pickup_location}
                          dropoff={o.dropoff_location}
                          providerLocation={o.provider_location}
                          rideStatus={o.status}
                          routePhase={['accepted', 'driver_on_way'].includes(o.status) ? 'driver_to_pickup' : 'pickup_to_dropoff'}
                          height="240px"
                          viewer="client"
                          onEtaChange={(eta) => handleRideEtaChange(o.id, eta)}
                        />
                      </div>
                    )}

                    {isRide && o.status === 'completed' && !o.rating_by_client && (
                      <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                        <p className="text-sm text-white">Califica tu viaje</p>
                        <div className="flex flex-wrap gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Button key={star} onClick={() => setRideRatings((current) => ({ ...current, [o.id]: { ...(current[o.id] || {}), rating: star } }))} className={`${Number(rideRatings[o.id]?.rating || 5) >= star ? 'gradient-button' : 'bg-white/5 border border-white/10 text-white'} rounded-xl px-3`}>
                              {star}
                            </Button>
                          ))}
                        </div>
                        <Input placeholder="Comentario opcional" value={rideRatings[o.id]?.comment || ''} onChange={(e) => setRideRatings((current) => ({ ...current, [o.id]: { ...(current[o.id] || { rating: 5 }), comment: e.target.value } }))} className="bg-white/5 border-white/10 text-white rounded-xl" />
                        <Button onClick={() => rateRide(o.id)} className="gradient-button rounded-xl">Enviar calificacion</Button>
                      </div>
                    )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Providers Search */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-white mb-3" style={{ fontFamily: 'Outfit' }}>
            Proveedores Verificados
          </h3>
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar proveedores..."
              className="pl-12 py-6 bg-white/5 border-white/10 rounded-2xl text-white placeholder:text-[#9CA3AF]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center text-[#9CA3AF] py-12">Cargando proveedores...</div>
          ) : filteredProviders.length === 0 ? (
            <div className="col-span-full text-center text-[#9CA3AF] py-12">No se encontraron proveedores</div>
          ) : (
            filteredProviders.map((provider) => (
              <div key={provider.id || provider.name} className="glass-card p-6 rounded-3xl hover:scale-[1.02] transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-white mb-1" style={{ fontFamily: 'Outfit' }}>
                      {provider.name}
                    </h3>
                    <p className="text-sm text-[#9CA3AF] capitalize">{provider.provider_type}</p>
                  </div>
                  <div className="flex items-center gap-1 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-lg">
                    <Star className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-green-400 font-medium">Verificado</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                  <MapPin className="w-4 h-4" />
                  <span>{provider.zone || 'Zona de cobertura registrada'}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      <RideRequestModal
        open={showRideModal}
        onOpenChange={setShowRideModal}
        onRideCreated={() => {
          loadMyOrders();
          showToast('Solicitud enviada', 'Estamos buscando taxistas cercanos.');
        }}
      />
      <DeliveryRequestModal
        open={showDeliveryModal}
        onOpenChange={setShowDeliveryModal}
        onOrderCreated={() => loadMyOrders()}
      />

      <Dialog open={simpleCancelDialog.open} onOpenChange={(open) => !open && closeSimpleCancelDialog()}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
              Cancelar solicitud
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <p className="text-sm text-[#D1D5DB]">
                Todavía no hay conductor asignado. Puedes cancelar esta solicitud sin penalización.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button
                type="button"
                onClick={closeSimpleCancelDialog}
                disabled={submittingCancel}
                className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-xl"
              >
                Volver
              </Button>
              <Button
                type="button"
                onClick={submitSimpleCancelRide}
                disabled={submittingCancel}
                className="bg-gradient-to-r from-red-500 to-[#FF007F] hover:opacity-90 text-white rounded-xl"
              >
                {submittingCancel ? 'Cancelando...' : 'Sí, cancelar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelDialog.open} onOpenChange={(open) => !open && closeCancelRideDialog()}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
              Cancelar viaje
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-2xl bg-red-500/10 border border-red-500/25 p-3">
              <p className="text-sm text-red-100 font-medium">Esta acción quedará registrada.</p>
              <p className="text-xs text-[#D1D5DB] mt-1">
                Si el viaje está en curso o marcas un motivo grave, el administrador podrá revisar el caso.
              </p>
            </div>

            <div>
              <label className="text-sm text-[#D1D5DB] block mb-2">Motivo</label>
              <select
                value={cancelDialog.reason}
                onChange={(event) => updateCancelDialog('reason', event.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-3 outline-none"
              >
                <option value="" className="bg-[#0B0C10]">Selecciona un motivo</option>
                {clientCancelReasons.map((reason) => (
                  <option key={reason.key} value={reason.key} className="bg-[#0B0C10]">
                    {reason.label}{reason.level === 'strong' ? ' · grave' : ''}
                  </option>
                ))}
              </select>
            </div>

            {clientCancelReasons.find((reason) => reason.key === cancelDialog.reason)?.level === 'strong' && (
              <div className="rounded-2xl bg-yellow-500/10 border border-yellow-500/25 p-3">
                <p className="text-sm text-yellow-100 font-medium">Motivo grave</p>
                <p className="text-xs text-[#D1D5DB] mt-1">
                  Describe claramente lo ocurrido. Puedes agregar un enlace de foto o audio si tienes evidencia.
                </p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <label className="text-sm text-[#D1D5DB]">Descripción</label>
                {cancelDialog.ride?.status === 'in_progress' && (
                  <span className={`text-xs ${getCancelDescriptionWordCount() >= 20 ? 'text-green-300' : 'text-yellow-300'}`}>
                    {getCancelDescriptionWordCount()}/20 palabras
                  </span>
                )}
              </div>
              <textarea
                value={cancelDialog.description}
                onChange={(event) => updateCancelDialog('description', event.target.value)}
                placeholder={cancelDialog.ride?.status === 'in_progress'
                  ? 'Describe qué está pasando con detalle. Mínimo 20 palabras.'
                  : 'Describe brevemente qué ocurrió.'}
                rows={5}
                className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#9CA3AF] rounded-xl px-3 py-3 outline-none resize-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-2">
              <select
                value={cancelDialog.evidence_type}
                onChange={(event) => updateCancelDialog('evidence_type', event.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-xl px-3 py-3 outline-none"
              >
                <option value="" className="bg-[#0B0C10]">Evidencia</option>
                <option value="photo" className="bg-[#0B0C10]">Foto</option>
                <option value="audio" className="bg-[#0B0C10]">Audio</option>
              </select>
              <Input
                value={cancelDialog.evidence_url}
                onChange={(event) => updateCancelDialog('evidence_url', event.target.value)}
                placeholder="Link de evidencia opcional"
                className="bg-white/5 border-white/10 text-white placeholder:text-[#9CA3AF] rounded-xl"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button
                type="button"
                onClick={closeCancelRideDialog}
                disabled={submittingCancel}
                className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-xl"
              >
                Volver
              </Button>
              <Button
                type="button"
                onClick={submitCancelRide}
                disabled={submittingCancel}
                className="bg-gradient-to-r from-red-500 to-[#FF007F] hover:opacity-90 text-white rounded-xl"
              >
                {submittingCancel ? 'Cancelando...' : 'Confirmar cancelación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(chatRide)} onOpenChange={(open) => !open && setChatRide(null)}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-lg rounded-3xl p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
              Chat con {chatRide?.provider_name || 'conductor'}
            </DialogTitle>
          </DialogHeader>
          {chatRide && (
            <div className="p-4">
              <ChatInterface
                orderId={chatRide.id}
                orderType="ride"
                otherPartyName={chatRide.provider_name || 'Conductor'}
                height="420px"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
